import { FC, useState } from 'react'
import moment from 'moment'
import { CustomCalendar } from 'src/App/AppRouter/Layout/TopBar/TopBarDatePicker/CustomCalendar'
import { ReactComponent as CalendarIcon } from './solar_calendar-outline.svg'
import classes from './filtersModal.module.scss'
import { AdFilters } from '../../index'

const ACTIVE_STATUS_OPTIONS = [
  'Active and inactive',
  'Active ads',
  'Inactive ads',
]
const LANGUAGE_OPTIONS = [
  'All languages',
  'English',
  'Georgian',
  'Russian',
  'Arabic',
  'French',
  'German',
  'Spanish',
]
const MEDIA_TYPE_OPTIONS = [
  'All media types',
  'Images',
  'Memes',
  'Images and memes',
  'Videos',
  'No image or video',
]
const PLATFORM_OPTIONS = [
  'All platforms',
  'Facebook',
  'Instagram',
  'Google',
  'LinkedIn',
]

const InfoTooltip: FC<{ text: string }> = ({ text }) => {
  const [visible, setVisible] = useState(false)
  return (
    <span
      className={classes.infoTooltipWrapper}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <svg
        fill="none"
        height="16"
        viewBox="0 0 24 24"
        width="16"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" fill="#8080FF" r="10" />
        <path
          d="M12 8v4M12 16h.01"
          stroke="#fff"
          strokeLinecap="round"
          strokeWidth="2"
        />
      </svg>
      {visible && <div className={classes.tooltip}>{text}</div>}
    </span>
  )
}

type DateField = 'from' | 'to' | null

interface FiltersModalProps {
  filters: AdFilters
  onClose: () => void
  onFiltersChange: (filters: AdFilters) => void
}

export const FiltersModal: FC<FiltersModalProps> = ({
  filters,
  onClose,
  onFiltersChange,
}) => {
  const [language, setLanguage] = useState(filters.language)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [languageSearch, setLanguageSearch] = useState('')
  const [platform, setPlatform] = useState(filters.platform)
  const [platformOpen, setPlatformOpen] = useState(false)
  const [mediaType, setMediaType] = useState(filters.mediaType)
  const [mediaTypeOpen, setMediaTypeOpen] = useState(false)
  const [activeStatus, setActiveStatus] = useState(filters.activeStatus)
  const [activeStatusOpen, setActiveStatusOpen] = useState(false)
  const [fromDate, setFromDate] = useState<moment.Moment | null>(null)
  const [toDate, setToDate] = useState<moment.Moment | null>(null)
  const [openCalendar, setOpenCalendar] = useState<DateField>(null)

  const activeFilterCount = [
    language !== 'All languages',
    platform !== 'All platforms',
    mediaType !== 'All media types',
    activeStatus !== 'Active ads',
    fromDate !== null || toDate !== null,
  ].filter(Boolean).length

  const handleClearAll = () => {
    setLanguage('All languages')
    setLanguageOpen(false)
    setLanguageSearch('')
    setPlatform('All platforms')
    setPlatformOpen(false)
    setMediaType('All media types')
    setMediaTypeOpen(false)
    setActiveStatus('Active ads')
    setActiveStatusOpen(false)
    setFromDate(null)
    setToDate(null)
    setOpenCalendar(null)
  }
  const handleCalendarApply = (start: moment.Moment, end: moment.Moment) => {
    if (openCalendar === 'from') {
      setFromDate(start)
    } else {
      setToDate(end)
    }
    setOpenCalendar(null)
  }

  const formatDate = (d: moment.Moment | null) =>
    d ? d.format('MMM D, YYYY') : ''

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.header}>
          <h2 className={classes.title}>Filters</h2>
          <button className={classes.closeBtn} onClick={onClose} type="button">
            <svg
              fill="none"
              height="20"
              viewBox="0 0 24 24"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="#25262D"
                strokeLinecap="round"
                strokeWidth="1.5"
              />
            </svg>
          </button>
        </div>

        <div className={classes.filters}>
          {/* Language */}
          <div className={classes.filterGroup}>
            <div className={classes.labelRow}>
              <span className={classes.label}>Language</span>
              <InfoTooltip text="Ads that contain more than one language will not appear in the results when this filter is applied." />
            </div>
            <button
              className={classes.customSelect}
              onClick={() => setLanguageOpen((v) => !v)}
              type="button"
            >
              <span>{language}</span>
              <svg
                fill="none"
                height="16"
                viewBox="0 0 24 24"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d={languageOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity="0.5"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
            {languageOpen && (
              <div className={classes.customDropdown}>
                <div className={classes.customDropdownInner}>
                  <div className={classes.searchRow}>
                    <input
                      className={classes.searchInput}
                      onChange={(e) => setLanguageSearch(e.target.value)}
                      placeholder="Search for language"
                      type="text"
                      value={languageSearch}
                    />
                    <svg
                      fill="none"
                      height="16"
                      viewBox="0 0 24 24"
                      width="16"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="11"
                        cy="11"
                        r="8"
                        stroke="#25262D"
                        strokeOpacity="0.3"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M21 21l-4.35-4.35"
                        stroke="#25262D"
                        strokeLinecap="round"
                        strokeOpacity="0.3"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>
                  {LANGUAGE_OPTIONS.filter((o) =>
                    o.toLowerCase().includes(languageSearch.toLowerCase())
                  ).map((option) => {
                    const isSelected = language === option
                    return (
                      <button
                        className={`${classes.customOption} ${isSelected ? classes.customOptionSelected : ''}`}
                        key={option}
                        onClick={() => {
                          setLanguage(option)
                          setLanguageOpen(false)
                          setLanguageSearch('')
                        }}
                        type="button"
                      >
                        <span
                          className={`${classes.checkbox} ${isSelected ? classes.checkboxSelected : ''}`}
                        >
                          {isSelected && (
                            <svg
                              fill="none"
                              height="10"
                              viewBox="0 0 12 12"
                              width="10"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M2 6l3 3 5-5"
                                stroke="#fff"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                              />
                            </svg>
                          )}
                        </span>
                        <span className={classes.customOptionLabel}>
                          {option}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Platform */}
          <div className={classes.filterGroup}>
            <div className={classes.labelRow}>
              <span className={classes.label}>Platform</span>
              <InfoTooltip text="As of October 2019, social icons indicate which platforms (e.g. Facebook, Instagram) the advertiser approved the ad to run on." />
            </div>
            <button
              className={classes.customSelect}
              onClick={() => setPlatformOpen((v) => !v)}
              type="button"
            >
              <span>{platform}</span>
              <svg
                fill="none"
                height="16"
                viewBox="0 0 24 24"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d={platformOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity="0.5"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
            {platformOpen && (
              <div className={classes.customDropdown}>
                <div className={classes.customDropdownInner}>
                  {PLATFORM_OPTIONS.map((option) => {
                    const isSelected = platform === option
                    return (
                      <button
                        className={`${classes.customOption} ${isSelected ? classes.customOptionSelected : ''}`}
                        key={option}
                        onClick={() => {
                          setPlatform(option)
                          setPlatformOpen(false)
                        }}
                        type="button"
                      >
                        <span
                          className={`${classes.checkbox} ${isSelected ? classes.checkboxSelected : ''}`}
                        >
                          {isSelected && (
                            <svg
                              fill="none"
                              height="10"
                              viewBox="0 0 12 12"
                              width="10"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M2 6l3 3 5-5"
                                stroke="#fff"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                              />
                            </svg>
                          )}
                        </span>
                        <span className={classes.customOptionLabel}>
                          {option}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Media type */}
          <div className={classes.filterGroup}>
            <span className={classes.label}>Media type</span>
            <button
              className={classes.customSelect}
              onClick={() => setMediaTypeOpen((v) => !v)}
              type="button"
            >
              <span>{mediaType}</span>
              <svg
                fill="none"
                height="16"
                viewBox="0 0 24 24"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d={mediaTypeOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity="0.5"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
            {mediaTypeOpen && (
              <div className={classes.customDropdown}>
                <div className={classes.customDropdownInner}>
                  {MEDIA_TYPE_OPTIONS.map((option) => {
                    const isSelected = mediaType === option
                    return (
                      <button
                        className={`${classes.customOption} ${isSelected ? classes.customOptionSelected : ''}`}
                        key={option}
                        onClick={() => {
                          setMediaType(option)
                          setMediaTypeOpen(false)
                        }}
                        type="button"
                      >
                        <span
                          className={`${classes.checkbox} ${isSelected ? classes.checkboxSelected : ''}`}
                        >
                          {isSelected && (
                            <svg
                              fill="none"
                              height="10"
                              viewBox="0 0 12 12"
                              width="10"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M2 6l3 3 5-5"
                                stroke="#fff"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                              />
                            </svg>
                          )}
                        </span>
                        <span className={classes.customOptionLabel}>
                          {option}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Active status */}
          <div className={classes.filterGroup}>
            <span className={classes.label}>Active status</span>
            <button
              className={classes.customSelect}
              onClick={() => setActiveStatusOpen((v) => !v)}
              type="button"
            >
              <span>{activeStatus}</span>
              <svg
                fill="none"
                height="16"
                viewBox="0 0 24 24"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d={activeStatusOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity="0.5"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
            {activeStatusOpen && (
              <div className={classes.customDropdown}>
                <div className={classes.customDropdownInner}>
                  {ACTIVE_STATUS_OPTIONS.map((option) => {
                    const isSelected = activeStatus === option
                    return (
                      <button
                        className={`${classes.customOption} ${isSelected ? classes.customOptionSelected : ''}`}
                        key={option}
                        onClick={() => {
                          setActiveStatus(option)
                          setActiveStatusOpen(false)
                        }}
                        type="button"
                      >
                        <span
                          className={`${classes.checkbox} ${isSelected ? classes.checkboxSelected : ''}`}
                        >
                          {isSelected && (
                            <svg
                              fill="none"
                              height="10"
                              viewBox="0 0 12 12"
                              width="10"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M2 6l3 3 5-5"
                                stroke="#fff"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                              />
                            </svg>
                          )}
                        </span>
                        <span className={classes.customOptionLabel}>
                          {option}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Impressions by date */}
          <div className={classes.filterGroup}>
            <span className={classes.label}>Impressions by date</span>
            <span className={classes.dateHint}>
              Choose an exact date or select a date range.
            </span>
            <div className={classes.dateRow}>
              <div className={classes.dateGroup}>
                <span className={classes.dateLabel}>From</span>
                <button
                  className={`${classes.dateInput} ${openCalendar === 'from' ? classes.dateInputActive : ''}`}
                  onClick={() =>
                    setOpenCalendar(openCalendar === 'from' ? null : 'from')
                  }
                  type="button"
                >
                  <CalendarIcon className={classes.calendarIcon} />
                  <span className={classes.dateInputText}>
                    {formatDate(fromDate)}
                  </span>
                </button>
                {openCalendar === 'from' && (
                  <div className={classes.inlineCalendar}>
                    <CustomCalendar
                      initialEnd={fromDate}
                      initialStart={fromDate}
                      onApply={handleCalendarApply}
                      onClose={() => setOpenCalendar(null)}
                    />
                  </div>
                )}
              </div>
              <div className={classes.dateGroup}>
                <span className={classes.dateLabel}>To</span>
                <button
                  className={`${classes.dateInput} ${openCalendar === 'to' ? classes.dateInputActive : ''}`}
                  onClick={() =>
                    setOpenCalendar(openCalendar === 'to' ? null : 'to')
                  }
                  type="button"
                >
                  <CalendarIcon className={classes.calendarIcon} />
                  <span className={classes.dateInputText}>
                    {formatDate(toDate)}
                  </span>
                </button>
                {openCalendar === 'to' && (
                  <div className={classes.inlineCalendar}>
                    <CustomCalendar
                      initialEnd={toDate}
                      initialStart={toDate}
                      onApply={handleCalendarApply}
                      onClose={() => setOpenCalendar(null)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={classes.footer}>
          <button
            className={classes.clearBtn}
            onClick={handleClearAll}
            type="button"
          >
            Cancel
          </button>
          <button
            className={classes.applyBtn}
            onClick={() => {
              onFiltersChange({ activeStatus, language, mediaType, platform })
              onClose()
            }}
            type="button"
          >
            {activeFilterCount > 0
              ? `Apply ${activeFilterCount} Filter${activeFilterCount > 1 ? 's' : ''}`
              : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  )
}
